import { CSSProperties } from "react";

/**
 * StreakPill — gold-toned streak indicator with a "Resting" grace state
 * instead of a hard reset to 0. When `resting` is true, streak number
 * is hidden in favor of "Resting · N day(s)" copy in verse-tone gold.
 *
 * Uses the brand gold tokens from index.css.
 */
export default function StreakPill({
  streak,
  resting = false,
  restingDays = 0,
  size = "md",
}: {
  streak: number;
  resting?: boolean;
  restingDays?: number;
  size?: "sm" | "md";
}) {
  const fontSize = size === "sm" ? 9.5 : 11;
  const padding = size === "sm" ? "3px 9px" : "4px 10px";

  const base: CSSProperties = {
    padding,
    borderRadius: 999,
    fontSize,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  };

  if (resting) {
    return (
      <span
        className="mnf-body"
        style={{
          ...base,
          background: "var(--mnf-gold2)",
          color: "var(--mnf-gold)",
          border: "1px solid rgba(160,120,32,0.18)",
        }}
        data-testid="streak-pill-resting"
      >
        <span aria-hidden>🌿</span>
        Resting · {restingDays} {restingDays === 1 ? "day" : "days"}
      </span>
    );
  }

  return (
    <span
      className="mnf-body"
      style={{
        ...base,
        background: "var(--mnf-gold2)",
        color: "var(--mnf-gold)",
      }}
      data-testid="streak-pill-active"
    >
      <span aria-hidden>✨</span>
      Streak · {streak} {streak === 1 ? "day" : "days"}
    </span>
  );
}

/**
 * Compact inline label for use inside dense list rows (e.g. Coach roster).
 * Renders just text in gold tone, no pill background.
 */
export function StreakInline({
  streak,
  resting = false,
  restingDays = 0,
}: {
  streak: number;
  resting?: boolean;
  restingDays?: number;
}) {
  if (resting) {
    return (
      <span
        className="mnf-body"
        style={{
          fontSize: 10.5,
          color: "var(--mnf-gold)",
          fontWeight: 700,
          letterSpacing: "0.02em",
        }}
        data-testid="streak-inline-resting"
      >
        Resting · {restingDays}d
      </span>
    );
  }
  return (
    <span
      className="mnf-body"
      style={{ fontSize: 10.5, color: "var(--mnf-tg)" }}
      data-testid="streak-inline-active"
    >
      {streak}d streak
    </span>
  );
}
